/* eslint-disable react-hooks/exhaustive-deps */
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HistoryIcon from "@mui/icons-material/History";
import { Box, Button, Divider, IconButton, Popover, Stack, Tooltip, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import moment from "moment";
import _ from "lodash";
import React, { useCallback, useEffect, useRef, useReducer, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { firstAndLastDateOfInput, getCensusFirstDate } from "../../../services/data-input.service";
import { setFilterDateRange as setAdmissionDateFilter } from "../../../store/reducers/admission.slice";
import {
	setCustomPercentage,
	setCustomPercentageLabel,
	setEndDateOfADT,
	setFilterDateUpdate,
	setIsManualFacilityChange,
	setLastADTDate,
	setPauseDateSlice,
	setShowMultipleFacilitiesPauseDialog,
	setPauseDialogShownForFacilities,
	setPauseUser,
	setSkipMainFetch,
	setEnableComparisonFetch,
} from "../../../store/reducers/common.slice";
import { setFilterDateRange as setCommunityDateFilter, setLoading as setCommunityLoading } from "../../../store/reducers/community-transfer.slice";
import { setFilterDateRange as setDescentDateFilter, setLoading as setDescentLoading } from "../../../store/reducers/deceased.slice";
import { setFilterDateRange as setHospitalDateFilter, setLoading as setHospitalLoading } from "../../../store/reducers/hospital.slice";
import { setFilterDateRange as setOverallDateFilter, setLoading as setOverallLoading } from "../../../store/reducers/overall.slice";
import { setLoading as setAdmissionLoading } from "../../../store/reducers/admission.slice";
import { setFilterDateRange as setQuickGlaceDateFilter } from "../../../store/reducers/quick-glace.slice";
import {
	dateFormat,
	toDisplayFullDate,
	toDisplayFullDateWithNextDay,
	toDisplayTime,
	toDisplayUTCDate,
	toSaveDate,
} from "../../../utilis/date-formats";
import { addDays, subDays } from "../../../utilis/date";
import DateRangePickerComponent from "./dateRangePicker/DateRangePickerComponent";
import ToastNotification from "../../shared/ToastNotification";
import NoOverlapDialog from "./NoOverlapDialog";
import { getEndDateOfADTData } from "../../../services/api/facility-manually-end-date-adt";
import { setIsComparisonRangeSet, setIsOverLapComparison, setIsOverLapDateMinimum, setManuallyClickComparison, setRemoveCompareFilter } from "../../../store/reducers/comparisonReducers/comparison.slice";
import useComparisonFunctions from "../../comparison-popup/useComparisonFunctions";
import TrendAnalysisOffDialog from "../../landing/facility/data/add-patient/TrendAnalysisOffDialog";
import { PAGE_TYPE } from "../../../types/pages.type";
import { useDashboard } from "../../../context/DashboardContext";
import MultipleFacilitiesPauseDateDialog from "../../shared/MultipleFacilitiesPauseDateDialog";
import aiDateActionsService from "../../../services/ai-date-actions.service";

const FilterContainer = styled(Box)(() => ({
	display: "flex",
	position: "relative",
	width: "100%",
	justifyContent: "space-evenly",
	background: "#FFFFFF",
	boxShadow: "0px 4px 14px rgba(0, 0, 0, 0.06)",
	borderRadius: "8px",
	height: "60px",
	alignItems: "center",
	padding: "15px 0px",
	"@media (max-width: 768px)": {
		height: "42px",
		flexDirection: "row",
		alignItems: "center",
		width: "100%",
		padding: "15px 0px",
		justifyContent: "space-between",
		gap: "10px",
	}
}));

// Module-level storage for manual date selection (persists across component remounts)
// This is more reliable than refs if the component gets remounted
let manualDateSelectionStorage = {
	isManual: false,
	dateRange: null
};

/**
 * Aligns module-level manual date tracking when dates are set outside the picker (e.g. page converter).
 * Normalizes to start-of-day like handleOnChange.
 */
export function primeManualDateSelectionForImportedRange(latestValue) {
	if (!latestValue?.startDate || !latestValue?.endDate) return;
	const normalized = {
		...latestValue,
		key: latestValue.key ?? "selection",
		startDate: moment(latestValue.startDate).startOf("day").toDate(),
		endDate: moment(latestValue.endDate).startOf("day").toDate(),
	};
	manualDateSelectionStorage.isManual = true;
	manualDateSelectionStorage.dateRange = normalized;
}

// Reducer for tracking manual date selection
const manualDateSelectionReducer = (state, action) => {
	switch (action.type) {
		case 'SET_MANUAL_SELECTION':
			return {
				isManual: true,
				dateRange: action.payload
			};
		case 'CLEAR_MANUAL_SELECTION':
			return {
				isManual: false,
				dateRange: null
			};
		case 'RESET':
			return {
				isManual: false,
				dateRange: null
			};
		default:
			return state;
	}
};

const DateFilterComponent = (props) => {
	const dispatch = useDispatch();
	const { filter, handleFilterUpdate, isLayoutMode = false } = props;
	const [isOPenDateRange, setIsOPenDateRange] = useState(false);
	const [state, setState] = useState([{ ...filter }]);
	const [censusDate, setCensusDate] = useState(null);
	const [pauseDate, setPauseDate] = useState(null);
	const [invalidDateMsg, setInvalidDateMsg] = useState(null);
	const [isTrendAnalysisOff, setIsTrendAnalysisOff] = useState(null);
	const [toastNotification, setToastNotification] = useState({
		open: false,
		type: 'error',
		title: '',
		message: '',
	});
	const activeFacilities = useSelector((state) => state.activeFacilities.facilities);
	const { isFilterDateUpdate, isManualFacilityChange } = useSelector((state) => state.common);
	const { isOverLapDateMinimum, isOverLapComparison, isComparisonRangeSet, rangesSet, comparisonRanges: ranges } = useSelector((state) => state.comparison);
	const [firstAndLastDate, setFirstAndLastDate] = useState(null);
	const [anchorElHistory, setAnchorElHistory] = useState(null);
	const [isOverLapDate, setIsOverLapDate] = useState(null);
	const [loadingComparison, setLoadingComparison] = useState(false); // eslint-disable-line
	const [censusData, setCensusData] = useState(null);
	const { showMultipleFacilitiesPauseDialog, pauseDialogShownForFacilities } = useSelector((state) => state.common);
	const { updateDashboard } = useDashboard();

	// Use reducer to track manual date selection (more reliable than refs)
	const [, dispatchManualDateSelection] = useReducer(manualDateSelectionReducer, {
		isManual: false,
		dateRange: null
	});

	// Keep refs as backup and for syncing with reducer state
	const previousDateRangeRef = useRef(null);
	const previousIsManualSelectionRef = useRef(false);
	const previousFacilitiesRef = useRef([]);

	// DO NOT sync reducer state to refs - refs are set directly and are the source of truth
	// The sync effect was overwriting refs when reducer state got cleared
	// Refs are now set directly in handleOnChange and persist reliably

	const { checkComparisonData } = useComparisonFunctions({
		forPage: props?.page,
		setLoadingComparison,
	});


	const handleClickHistory = (event) => {
		setAnchorElHistory(event.currentTarget);
	};

	const handleCloseHistory = () => {
		setAnchorElHistory(null);
	};
	const openHistoryPopover = Boolean(anchorElHistory);
	const idHistory = openHistoryPopover ? "simple-popover" : undefined;

	// Helper function to check if date range falls within facility's available range
	const isDateRangeWithinFacilityRange = (dateRange, facilityRange) => {
		if (!dateRange || !facilityRange || !facilityRange.fromDate || !facilityRange.toDate) {
			return false;
		}

		// Normalize dates to UTC and compare at day level to avoid timezone issues
		// Use startOf('day') to ensure we're comparing only the date portion, not time
		const rangeStart = moment(dateRange.startDate).utc().startOf('day');
		const rangeEnd = moment(dateRange.endDate).utc().startOf('day');
		const facilityStart = moment(facilityRange.fromDate).utc().startOf('day');
		const facilityEnd = moment(facilityRange.toDate).utc().startOf('day');

		// Check if the previously selected range falls within the new facility's available range
		// Start date should be on or after facility's fromDate, and end date should be on or before facility's toDate
		const isValid = rangeStart.isSameOrAfter(facilityStart, 'day') && rangeEnd.isSameOrBefore(facilityEnd, 'day');
		return isValid;
	};

	const fetchFirstAndLastDateOfInput = async (endDateOfADT, pauseDate = null, isFacilityChange = false, endDateOfADTResponse = null, facilitiesChangedFlag = false, previousFacilitiesLengthBeforeUpdate = 0) => {

		if (activeFacilities.length > 0) {
			const firstAndLastData = await firstAndLastDateOfInput({
				facilityIds: activeFacilities,
			});

			if (firstAndLastData) {
				dispatch(setCustomPercentage(firstAndLastData?.customPercentage));
				dispatch(setCustomPercentageLabel(firstAndLastData?.customPercentageLabel));
				let toDate = endDateOfADT && moment(firstAndLastData?.toDate).isBefore(endDateOfADT) ? endDateOfADT : firstAndLastData?.toDate;

				// Check if pauseDate exists and is before (earlier than) the calculated toDate
				// If pauseDate is earlier, use it as the maximum allowed date
				if (pauseDate && moment(pauseDate).isBefore(toDate)) {
					toDate = pauseDate;
				}

				firstAndLastData.toDate = toDate;
				dispatch(setLastADTDate(toDate));
			}

			setFirstAndLastDate(firstAndLastData);

			// Validate date range on manual facility change
			// When isFilterDateUpdate is true, it means handleFacilityRun was called (manual facility change via "Run" button)
			// Check if we have a previous manual date selection
			// Use refs first (synced from reducer, persist reliably across async operations), then fallback to filter or state
			const previousRangeFromRef = previousDateRangeRef.current;
			const previousRangeFromFilter = filter && filter.startDate && filter.endDate ? filter : null;
			const previousRangeFromState = state && state[0] && state[0].startDate && state[0].endDate ? state[0] : null;
			// Prefer refs (synced from reducer), then filter, then state
			const previousRange = previousRangeFromRef || previousRangeFromFilter || previousRangeFromState;

			// Check if we had a manual date selection - use module storage first (persists across remounts), then refs
			const hasManualFromModuleStorage = manualDateSelectionStorage.isManual && manualDateSelectionStorage.dateRange;
			const hasManualFromRefs = previousIsManualSelectionRef.current || !!previousRangeFromRef;
			const isManualDateSelection = hasManualFromModuleStorage || hasManualFromRefs;
			// Simple and reliable logic using isManualFacilityChange flag:
			// isManualFacilityChange is set to true ONLY when handleFacilityRun is called (Run button clicked)
			// This is the most reliable indicator of manual facility change
			// Validate if:
			// - isManualFacilityChange is true (Run button was clicked - manual facility change)
			// - AND we have a previous range (user had selected dates)
			// - AND user manually changed the date (isManualDateSelection from reducer is true)
			// If user did NOT manually change date, skip validation (use default dates for new facility)
			const shouldValidate = Boolean(
				isManualFacilityChange &&
				!!previousRange &&
				previousRange.startDate &&
				previousRange.endDate &&
				isManualDateSelection // Use reducer state (most reliable)
			);

			// Reset the flag after checking
			if (isManualFacilityChange) {
				dispatch(setIsManualFacilityChange(false));
			}
			if (shouldValidate) {
				const previousRangeToValidate = previousRange;
				// Use firstAndLastData for facility range (it has fromDate and toDate)
				// Also check if endDateOfADTResponse has fromDate and toDate fields
				let newFacilityRange = {
					fromDate: firstAndLastData?.fromDate,
					toDate: firstAndLastData?.toDate
				};

				// If getEndDateOfADTData response has fromDate and toDate, use those
				if (endDateOfADTResponse && endDateOfADTResponse.fromDate && endDateOfADTResponse.toDate) {
					newFacilityRange = {
						fromDate: endDateOfADTResponse.fromDate,
						toDate: endDateOfADTResponse.toDate
					};
				}

				// Check if previous date range falls within new facility's range
				const isWithinRange = isDateRangeWithinFacilityRange(previousRangeToValidate, newFacilityRange);
				if (!isWithinRange) {
					// Show alert and reset to default date range
					setInvalidDateMsg(
						`The previously selected date range is not available for the newly selected facility(s). The date range has been reset to the default range for the new facility(s).`
					);
					// Reset manual selection tracking (module storage, refs, and reducer)
					// Clear module storage (persists across remounts)
					manualDateSelectionStorage.isManual = false;
					manualDateSelectionStorage.dateRange = null;
					// Clear refs
					previousIsManualSelectionRef.current = false;
					previousDateRangeRef.current = null;
					// Clear reducer
					dispatchManualDateSelection({ type: 'CLEAR_MANUAL_SELECTION' });
					// Continue with default date range logic below
				} else {
					// Date range is valid, keep the previous selection
					setState([previousRangeToValidate]);
					updateDashboard({ filter: previousRangeToValidate });
					// Keep isFilterDateUpdate as false since we're maintaining a manual selection
					dispatch(setFilterDateUpdate(false));
					// Update reducer to track this as manual selection (date range is valid, so keep it)
					dispatchManualDateSelection({ type: 'SET_MANUAL_SELECTION', payload: previousRangeToValidate });
					const defaultComparison = await defaultComparisonRange(previousRangeToValidate, firstAndLastData, true, pauseDate);
					if (defaultComparison?.comparisonFilter) {
						await updateDateToAllDashboard({ ...previousRangeToValidate, defaultComparison: null });
						setTimeout(() => {
							handleFilterUpdate && handleFilterUpdate(true);
							dispatch(setManuallyClickComparison({ range: defaultComparison?.comparisonFilter, isDefault: true }));
						}, props.page === PAGE_TYPE.ADMISSION ? 0 : 100);
					} else {
						handleFilterUpdate && handleFilterUpdate(true);
						await updateDateToAllDashboard({ ...previousRangeToValidate, defaultComparison: null });
					}
					return; // Exit early, don't apply default dates
				}
			} else {
				// DO NOT clear reducer state when skipping validation
				// The refs are already synced and will persist
				// Only clear when validation shows alert (date out of bounds)
			}

			if (isFilterDateUpdate) {
				if (!firstAndLastData?.isAdtData) {
					// Calculate startDate: 30 days before endDateOfADT if endDateOfADT exists, otherwise use current date
					let startDateValue;
					if (endDateOfADT) {
						// Subtract 30 days from endDateOfADT
						const endDateMoment = moment(endDateOfADT);
						const startDateMoment = endDateMoment.clone().subtract(30, 'days');
						startDateValue = toDisplayUTCDate(startDateMoment.toDate());
					} else {
						startDateValue = toDisplayUTCDate(new Date());
					}

					let latestDate = {
						endDate: endDateOfADT ? endDateOfADT : toDisplayUTCDate(new Date()),
						key: "selection",
						startDate: startDateValue,
					};

					setState([latestDate]);
					handleFilterUpdate && handleFilterUpdate(true);
					await updateDateToAllDashboard({ ...latestDate, defaultComparison: null });

					setIsOverLapDate("No data entered for this facility yet");
				} else if (firstAndLastData && firstAndLastData.isOverlap === true) {

					let countDayData = moment.utc(firstAndLastData.toDate).diff(firstAndLastData.fromDate, "days");
					const countDay = Math.abs(countDayData);
					let latestDate;
					if (countDay > 30 || countDayData < 0) {
						latestDate = {
							endDate: toDisplayUTCDate(firstAndLastData.toDate),
							key: "selection",
							startDate: moment(toDisplayUTCDate(firstAndLastData.toDate)).subtract(29, "days").toDate(),
						};
					} else {
						let fromDate = firstAndLastData?.censusDate ? toDisplayUTCDate(firstAndLastData.censusDate) : toDisplayUTCDate(firstAndLastData.fromDate);
						latestDate = {
							endDate: toDisplayUTCDate(firstAndLastData.toDate),
							key: "selection",
							startDate: fromDate,
						};
					}

					setState([latestDate]);
					updateDashboard({ filter: latestDate });
					const defaultComparison = await defaultComparisonRange(latestDate, firstAndLastData, true, pauseDate);
					if (defaultComparison?.comparisonFilter) {

						await updateDateToAllDashboard({ ...latestDate, defaultComparison: null });
						setTimeout(() => {
							handleFilterUpdate && handleFilterUpdate(true);
							dispatch(setManuallyClickComparison({ range: defaultComparison?.comparisonFilter, isDefault: true }));
						}, props.page === PAGE_TYPE.ADMISSION ? 0 : 100);
					} else {
						handleFilterUpdate && handleFilterUpdate(true);
						await updateDateToAllDashboard({ ...latestDate, defaultComparison: null });
					}
				}
			} else {
				const defaultComparison = await defaultComparisonRange(filter, firstAndLastData, true, pauseDate);
				if (defaultComparison?.comparisonFilter) {
					await updateDateToAllDashboard({ ...filter, defaultComparison: null });
					setTimeout(() => {
						handleFilterUpdate && handleFilterUpdate(true);
						dispatch(setManuallyClickComparison({ range: defaultComparison?.comparisonFilter, isDefault: true }));
					}, props.page === PAGE_TYPE.ADMISSION ? 0 : 100);
				} else {
					handleFilterUpdate && handleFilterUpdate(true);
					await updateDateToAllDashboard({ ...filter, defaultComparison: null });
				}
			}
		}
	};

	const defaultComparisonRange = async (latestDate, firstAndLastDataDb = null, isDefault = false, pauseDate = null) => {

		if (!isComparisonRangeSet) {
			dispatch(setManuallyClickComparison(null));
			dispatch(setRemoveCompareFilter(false));
			return null
		}
		if (isDefault && rangesSet && ranges.length > 0) {
			const selectedRange = ranges[0];
			const comparisonFilterDefault = [
				{
					startDate: selectedRange?.startDate instanceof Date ? selectedRange.startDate.toISOString() : selectedRange?.startDate,
					endDate: selectedRange?.endDate instanceof Date ? selectedRange.endDate.toISOString() : selectedRange?.endDate,
					key: "selection",
				},
			];
			return { comparisonFilter: comparisonFilterDefault, isDefault }
		}

		const firstAndLastData = firstAndLastDataDb ? firstAndLastDataDb : firstAndLastDate;
		const censusDateDB = firstAndLastData?.fromDate ? firstAndLastData?.fromDate : censusDate;
		let defaultStartDate;
		let defaultEndDate;

		let countDayData = moment.utc(firstAndLastData.toDate).diff(firstAndLastData.fromDate, "days");
		const countDay = Math.abs(countDayData);


		if (countDay > 30 || countDayData < 0) {
			defaultStartDate = moment(toDisplayUTCDate(firstAndLastData.toDate)).subtract(29, "days").toDate();
			defaultEndDate = toDisplayUTCDate(firstAndLastData.toDate);
		} else {
			let fromDate = firstAndLastData?.censusDate ? toDisplayUTCDate(firstAndLastData.censusDate) : toDisplayUTCDate(firstAndLastData.fromDate);
			defaultStartDate = fromDate;
			defaultEndDate = toDisplayUTCDate(firstAndLastData.toDate);
		}

		// Check if pauseDate exists and is before the calculated defaultEndDate
		// If pauseDate is earlier, use it as the maximum allowed date for comparison
		if (pauseDate && moment(pauseDate).isBefore(defaultEndDate)) {
			defaultEndDate = pauseDate;
		}

		const selectedRange = await checkComparisonData(latestDate.startDate, latestDate.endDate, censusDateDB, {
			defaultStartDate,
			defaultEndDate,
		});

		if (selectedRange === "no overlap") {
			if (!isOverLapComparison) {
				setIsTrendAnalysisOff(`Trend Analysis is unavailable because the data you’re viewing is over 30 days old. To re-enable this feature, include more recent data in your view.`);
				setTimeout(() => {
					dispatch(setIsOverLapComparison(true));
				}, 200);
			}
			dispatch(setManuallyClickComparison(null));
			dispatch(setRemoveCompareFilter(false));
			return null
		} else if (selectedRange === "no minimum range") {
			if (!isOverLapDateMinimum) {
				setTimeout(() => {
					dispatch(setIsOverLapDateMinimum(true));
				}, 200);
				setIsTrendAnalysisOff(`Trend analysis is unavailable because we do not have sufficient data from before your selected dates to provide a comprehensive analysis. To re-enable this feature automatically, please select a date range that includes at least the same amount of data for this facility prior to your selected date range.`);
			}
			dispatch(setManuallyClickComparison(null));
			dispatch(setRemoveCompareFilter(false));
			return null
		} else if (selectedRange) {
			const comparisonFilter = [
				{
					startDate: selectedRange.startDate instanceof Date ? selectedRange.startDate.toISOString() : selectedRange.startDate,
					endDate: selectedRange.endDate instanceof Date ? selectedRange.endDate.toISOString() : selectedRange.endDate,
					key: "selection",
				},
			];
			return { comparisonFilter, isDefault }
		} else {
			dispatch(setManuallyClickComparison(null));
			dispatch(setRemoveCompareFilter(false));
			return null
		}
	};

	useEffect(() => {
		const fetchCensusDate = async () => {
			const facilityId = activeFacilities[activeFacilities.length - 1];
			const censusData = await getCensusFirstDate(facilityId, activeFacilities);
			if (censusData?.pauseDate) {
				setPauseDate(censusData?.pauseDate);
				dispatch(setPauseDateSlice(censusData?.pauseDate));
				dispatch(setPauseUser(censusData));
				// Show dialog if multiple facilities have pause dates and dialog hasn't been shown yet for this facility combination
				const currentFacilitiesKey = [...activeFacilities].sort().join(',');
				if (censusData?.isPauseDateFound && pauseDialogShownForFacilities !== currentFacilitiesKey) {
					dispatch(setShowMultipleFacilitiesPauseDialog(true));
					// Don't set pauseDialogShownForFacilities here - only set it when dialog is closed
				}
				return censusData?.pauseDate;
			} else {
				setPauseDate(null);
				dispatch(setPauseDateSlice(null));
				dispatch(setPauseUser(censusData));
			}

			if (censusData?.date) {
				setCensusDate(censusData.date);
				setCensusData(censusData);
			}
			return null;
		};

		const fetchData = async () => {
			if (activeFacilities && activeFacilities.length > 0) {
				// Check if this is a manual facility change (not default facility selection)
				// Compare current facilities with previous facilities to detect actual changes
				const previousFacilities = previousFacilitiesRef.current;
				const facilitiesChanged = previousFacilities.length > 0 &&
					!_.isEqual(_.sortBy(previousFacilities), _.sortBy(activeFacilities));

				// isFilterDateUpdate is set to true when handleFacilityRun is called (manual facility change)
				// If isFilterDateUpdate is true, it means user clicked "Run" button = manual facility change
				// We should validate if:
				// 1. isFilterDateUpdate is true (manual change triggered)
				// 2. AND we have a previous manual date selection
				// 3. AND (facilities changed OR this is not the first load with previous facilities)
				// Use module storage first (persists across remounts), then fallback to refs
				const hasManualFromModuleStorage = manualDateSelectionStorage.isManual && manualDateSelectionStorage.dateRange;
				const hasManualFromRefs = previousIsManualSelectionRef.current && previousDateRangeRef.current;
				const hasPreviousManualDateSelection = hasManualFromModuleStorage || hasManualFromRefs;
				const isFacilityChange = isFilterDateUpdate && hasPreviousManualDateSelection &&
					(facilitiesChanged || previousFacilities.length > 0);

				const pauseDate = await fetchCensusDate();
				const res = await getEndDateOfADTData({ facilityIds: activeFacilities });
				// if (!isMounted) return;

				let earliestDateData = null;
				if (res && res.latestEndDateOfADTManually.length > 0) {
					earliestDateData = res.latestEndDateOfADTManually?.reduce((earliest, current) => {
						return moment(earliest).isBefore(moment(current.endDateOfADT)) ? earliest : current.endDateOfADT;
					}, res.latestEndDateOfADTManually[0].endDateOfADT);
				}
				if (earliestDateData) {
					dispatch(setEndDateOfADT(earliestDateData));
				}
				// Pass the full response to check for fromDate and toDate fields
				// Pass facilitiesChanged flag and previousFacilitiesLength BEFORE updating the ref, so validation can use correct values
				fetchFirstAndLastDateOfInput(earliestDateData, pauseDate, isFacilityChange, res, facilitiesChanged, previousFacilities.length);

				// Update previous facilities reference for next comparison (AFTER validation check)
				previousFacilitiesRef.current = [...activeFacilities];
			}
		};
		fetchData();
	}, [activeFacilities]);

	const handleOnClickChooseDate = useCallback(() => {
		setIsOPenDateRange((prevState) => !prevState);
	}, []);

	const handleOnChange = useCallback(
		async (selection) => {
			let value = selection?.[0];

			const censusDateUI = firstAndLastDate?.fromDate ? toDisplayFullDate(firstAndLastDate?.fromDate) : censusDate;
			const censusFormatDate = firstAndLastDate?.fromDate ? toDisplayFullDate(firstAndLastDate?.fromDate) : toDisplayFullDate(censusDate);
			const isInvalidDate = moment(toSaveDate(selection?.[0].startDate)).isBefore(censusFormatDate);
			const isBeyondPauseDate = pauseDate && moment(toSaveDate(selection?.[0].endDate)).isAfter(pauseDate);
			dispatch(setIsComparisonRangeSet(true));
			if (isInvalidDate) {
				setInvalidDateMsg(
					`we are unable to show you the data for the selected dates because it is prior to the initial input date ${toDisplayTime(
						censusDateUI
					)}. Please select dates that are after the initial input date`
				);
			} else if (isBeyondPauseDate) {
				setInvalidDateMsg(
					`we are unable to show you the data for the selected dates because it extends beyond your pause date ${toDisplayTime(
						pauseDate
					)}. Please select dates that are before or on your pause date`
				);
			} else {
				updateLoadingToAllDashboard(true);
				setIsOPenDateRange(false);
				setState(selection);
				dispatch(setFilterDateUpdate(false));
				let latestValue = {
					...value,
					startDate: moment(value?.startDate).startOf("day").toDate(),
					endDate: moment(value?.endDate).startOf("day").toDate(),
				};
				// Track manual date selection for facility change validation
				// Set refs DIRECTLY (they persist reliably) AND update reducer (for consistency)
				// Set refs DIRECTLY first (they persist even if reducer gets cleared)
				previousIsManualSelectionRef.current = true;
				previousDateRangeRef.current = latestValue;

				// ALSO set module-level storage (persists across component remounts)
				manualDateSelectionStorage.isManual = true;
				manualDateSelectionStorage.dateRange = latestValue;

				// Also update reducer for consistency (but refs are the source of truth)
				dispatchManualDateSelection({ type: 'SET_MANUAL_SELECTION', payload: latestValue });
				const defaultComparison = await defaultComparisonRange(latestValue);

				if (defaultComparison?.comparisonFilter) {
					// When comparison is triggered manually, skip the main data fetch
					// to avoid duplicate API calls (comparison fetch will run next)
					dispatch(setSkipMainFetch(true));
					await updateDateToAllDashboard({ ...latestValue, defaultComparison: null });
					setTimeout(() => {
						dispatch(setEnableComparisonFetch(true));
						handleFilterUpdate && handleFilterUpdate(true);
						// Apply comparison range (this will trigger comparison data fetch)
						dispatch(setManuallyClickComparison({ range: defaultComparison?.comparisonFilter, isDefault: true }));

						// Re-enable main data fetch after comparison is set
						dispatch(setSkipMainFetch(false));
						dispatch(setEnableComparisonFetch(false));
					}, props.page === PAGE_TYPE.ADMISSION ? 0 : 100);
				} else {
					updateDashboard({ filter: latestValue });
					await updateDateToAllDashboard({ ...latestValue, defaultComparison: null });
				}
			}
			dispatch(setIsOverLapDateMinimum(false));
			dispatch(setIsOverLapComparison(false));
		},
		[censusDate, firstAndLastDate, defaultComparisonRange]
	);

	// Store handleOnChange in a ref to always have the latest version
	const handleOnChangeRef = useRef(handleOnChange);
	useEffect(() => {
		handleOnChangeRef.current = handleOnChange;
	}, [handleOnChange]);

	// Expose handleOnChange to AI service for action execution
	// Only runs once on mount/unmount, but always uses the latest handler via ref
	useEffect(() => {
		// Create a wrapper function that calls the latest handleOnChange from ref
		const handlerWrapper = (selection) => {
			handleOnChangeRef.current(selection);
		};
		aiDateActionsService.setDateFilterHandler(handlerWrapper);
		return () => {
			// Cleanup on unmount
			aiDateActionsService.setDateFilterHandler(null);
		};
	}, []); // Empty dependency array - only runs on mount/unmount

	useEffect(() => {
		// Safety reset: always allow main fetch when this component mounts
		dispatch(setSkipMainFetch(false));

		dispatch(setEnableComparisonFetch(false));

		return () => {
			// Optional cleanup (extra safety)
			dispatch(setSkipMainFetch(false));
			dispatch(setEnableComparisonFetch(false));
		};
	}, []);

	const updateDateToAllDashboard = useCallback(
		(value) => {
			dispatch(setHospitalDateFilter(value));
			dispatch(setCommunityDateFilter(value));
			dispatch(setAdmissionDateFilter(value));
			dispatch(setDescentDateFilter(value));
			dispatch(setOverallDateFilter(value));
			dispatch(setQuickGlaceDateFilter(value));
		},
		[dispatch]
	);

	const updateLoadingToAllDashboard = useCallback(
		(value) => {
			dispatch(setHospitalLoading(value));
			dispatch(setOverallLoading(value));
			dispatch(setDescentLoading(value));
			dispatch(setCommunityLoading(value));
			dispatch(setAdmissionLoading(value));
		},
		[dispatch]
	);

	const handleOnClose = useCallback(() => {
		setIsOPenDateRange((prevState) => !prevState);
	}, []);

	const handleArrowClick = useCallback(
		async (type) => {
			dispatch(setIsComparisonRangeSet(true));
			if (state && state.length > 0) {
				const mkey = state[0]["key"];
				const mStartDate = state[0]["startDate"];
				const mEndDate = state[0]["endDate"];
				const date1 = moment(mEndDate);
				const date2 = moment(mStartDate);
				const days = date1.diff(date2, "days");

				if (days > 0) {
					if (type === "prev") {
						const newStartDatePrev = subDays(mStartDate, days);
						const newEndDatePrev = subDays(mEndDate, days);
						const mObject = {
							key: mkey,
							startDate: newStartDatePrev,
							endDate: newEndDatePrev,
						};
						setState([mObject]);
						const defaultComparison = await defaultComparisonRange(mObject);
						await updateDateToAllDashboard({ ...mObject, defaultComparison });
					} else {
						// For "next" arrow, check if the new end date would exceed the pause date
						const newStartDateNext = addDays(mStartDate, days);
						const newEndDateNext = addDays(mEndDate, days);

						// Check if pauseDate exists and if the new end date would exceed it
						if (pauseDate && moment(newEndDateNext).isAfter(pauseDate)) {
							// Calculate the maximum allowed end date (pause date)
							const maxAllowedEndDate = moment(pauseDate).startOf('day').toDate();
							const maxAllowedStartDate = subDays(maxAllowedEndDate, days);

							const mObject = {
								key: mkey,
								startDate: maxAllowedStartDate,
								endDate: maxAllowedEndDate,
							};

							setState([mObject]);
							const defaultComparison = await defaultComparisonRange(mObject);
							if (defaultComparison?.comparisonFilter) {
								await updateDateToAllDashboard({ ...mObject, defaultComparison: null });
								setTimeout(() => {
									handleFilterUpdate && handleFilterUpdate(true);
									dispatch(setManuallyClickComparison({ range: defaultComparison?.comparisonFilter, isDefault: true }));
								}, props.page === PAGE_TYPE.ADMISSION ? 0 : 100);
							} else {
								await updateDateToAllDashboard({ ...mObject, defaultComparison: null });
							}
						} else {
							// Normal case - no pause date restriction
							const mObject = {
								key: mkey,
								startDate: newStartDateNext,
								endDate: newEndDateNext,
							};

							setState([mObject]);
							const defaultComparison = await defaultComparisonRange(mObject);
							if (defaultComparison?.comparisonFilter) {
								await updateDateToAllDashboard({ ...mObject, defaultComparison: null });
								setTimeout(() => {
									handleFilterUpdate && handleFilterUpdate(true);
									dispatch(setManuallyClickComparison({ range: defaultComparison?.comparisonFilter, isDefault: true }));
								}, props.page === PAGE_TYPE.ADMISSION ? 0 : 100);
							} else {
								await updateDateToAllDashboard({ ...mObject, defaultComparison: null });
							}
						}
					}
				}
				dispatch(setIsOverLapDateMinimum(false));
				dispatch(setIsOverLapComparison(false));
			}
		},
		[state, updateDateToAllDashboard, censusDate, firstAndLastDate, pauseDate]
	);

	// Memoize the toast close handler to prevent timer reset on scroll/re-render
	const handleToastClose = useCallback(() => {
		setToastNotification(prev => ({ ...prev, open: false }));
		setInvalidDateMsg(null);
	}, []);

	// Show toast notification when invalidDateMsg is set
	useEffect(() => {
		if (invalidDateMsg) {
			setToastNotification({
				open: true,
				type: 'warning', // Changed from 'success' to 'warning' for neutral grey color
				title: 'Date Validation Error',
				message: invalidDateMsg,
			});
		}
	}, [invalidDateMsg]);

	// Early returns for conditional dialogs
	if (isTrendAnalysisOff) {
		return (
			<>
				<TrendAnalysisOffDialog
					selectedItem={{ errors: [isTrendAnalysisOff] }}
					close={() => setIsTrendAnalysisOff(null)}
					hideHeader={true}
				/>
			</>
		);
	}

	return (
		<>
			<FilterContainer className={props.loading && "skeleton"} style={{ cursor: isLayoutMode ? "default" : "pointer", pointerEvents: isLayoutMode ? "none" : "auto", opacity: isLayoutMode ? 0.5 : 1 }}>
				{!props.loading && (
					<>
						{!props?.isMobile && (
							<>
								<Tooltip title="Displays the date range of data entry" arrow>
									<IconButton
										aria-describedby={idHistory}
										variant="contained"
										onClick={handleClickHistory}
										color="primary"
										component="label"
										disableRipple
										sx={{ padding: { lg: "8px", md: "4px" } }}
									>
										<HistoryIcon />
									</IconButton>
								</Tooltip>

								<Popover
									id={idHistory}
									open={openHistoryPopover}
									anchorEl={anchorElHistory}
									onClose={handleCloseHistory}
									anchorOrigin={{
										vertical: "bottom",
										horizontal: "right",
									}}
								>
									{!firstAndLastDate?.isAdtData ? (
										<Typography sx={{ p: 2 }} variant="subtitle1">
											{activeFacilities.length > 1
												? "There is data that does not overlap for all facilities"
												: "No data entered for this facility yet"}
										</Typography>
									) : (
										<Typography sx={{ p: 2 }} variant="subtitle1">
											Start date of ADT : {toDisplayTime(firstAndLastDate?.fromDate)} <br />
											End date of ADT : {toDisplayTime(firstAndLastDate?.toDate)} <br />
										</Typography>
									)}
								</Popover>

								<Button onClick={handleOnClickChooseDate} sx={{ fontSize: { md: 13, lg: 12 } }}>
									Choose Date
								</Button>

								<Divider orientation="vertical" flexItem />
							</>
						)}

						<Stack direction={"row"} spacing={0} alignItems={"center"} sx={{
							"@media (max-width: 768px)": {
								ml: "15%"
							}
						}}>
							{!props?.isMobile && (
								<IconArrowButton
									disabled={isOPenDateRange}
									handleClickArrow={() => handleArrowClick("prev")}
									icon={<ChevronLeftIcon />}
								/>
							)}
							<Stack
								direction={"row"}
								alignItems={"center"}
								onClick={handleOnClickChooseDate}
								sx={{ cursor: "pointer" }}
							>
								<IconButton size="medium" color="primary" variant="contained" disableRipple>
									<CalendarMonthIcon fontSize="medium" />
								</IconButton>
								<Typography sx={{ fontSize: { xs: 13, md: 14, lg: 16 } }}>{filter && filter.startDate ? dateFormat(filter?.startDate) : "MM/DD/YYYY"}</Typography>
								<Typography sx={{ ml: { xs: 1, md: 1, lg: 2 }, mr: { xs: 1, md: 1, lg: 2 } }}>-</Typography>
								<Typography sx={{ fontSize: { xs: 13, md: 14, lg: 16 }, mr: 1 }}>
									{filter && filter.endDate ? dateFormat(filter?.endDate) : "MM/DD/YYYY"}
								</Typography>
							</Stack>
							{!props?.isMobile && (
								<IconArrowButton
									disabled={isOPenDateRange}
									handleClickArrow={() => handleArrowClick("next")}
									icon={<ChevronRightIcon />}
								/>
							)}
						</Stack>
						{isOPenDateRange && (
							<DateRangePickerComponent
								setIsOPenDateRange={setIsOPenDateRange}
								onChange={handleOnChange}
								selectedDate={state}
								handleCancel={handleOnClose}
								firstAndLastDate={firstAndLastDate}
								isMobile={props?.isMobile}
								maxDate={pauseDate ? toDisplayFullDateWithNextDay(pauseDate) : undefined}
							/>
						)}
					</>
				)}
			</FilterContainer>
			{isOverLapDate && (
				<NoOverlapDialog
					isOverLapDate={isOverLapDate}
					activeFacilities={activeFacilities}
					handleClose={() => setIsOverLapDate(null)}
				/>
			)}

			{showMultipleFacilitiesPauseDialog && (
				<MultipleFacilitiesPauseDateDialog
					open={true}
					onClose={() => {
						dispatch(setShowMultipleFacilitiesPauseDialog(false));
						// Mark this facility combination as having shown the dialog
						const currentFacilitiesKey = [...activeFacilities].sort().join(',');
						dispatch(setPauseDialogShownForFacilities(currentFacilitiesKey));
					}}
					pauseDate={censusData?.pauseDate}
					facilityPauseDates={censusData?.facilityPauseDate || []}
				/>
			)}
			<ToastNotification
				open={toastNotification.open}
				type={toastNotification.type}
				title={toastNotification.title}
				message={toastNotification.message}
				duration={7000}
				showOkButton={true}
				onClose={handleToastClose}
			/>
		</>
	);
};

export default DateFilterComponent;

const IconArrowButton = ({ icon, handleClickArrow, disabled }) => {
	return (
		<IconButton
			disabled={disabled}
			color="primary"
			disableRipple
			onClick={handleClickArrow}
			sx={{
				background: "#4879F5",
				color: "#fff",
				width: 20,
				height: 20,
			}}
		>
			{icon}
		</IconButton>
	);
};
